
import { Link, useNavigate } from '@/lib/router'
import { AlertTriangle, ArrowRight, Clock, HandCoins, PhoneCall } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Kpi } from '@/components/ui/kpi'
import { SkeletonRows } from '@/components/ui/skeleton'
import { bucketLabel, dpd, inr } from '@/lib/format'
import { cn } from '@/lib/utils'
import { FreshnessBadge } from '@/features/freshness/FreshnessBadge'

import { useTeleQueue, useTeleScorecard, type QueueReason, type TeleQueueRow } from './api'
import { isDefaultLanguage, languageLabel } from './languages'

const REASON_LABEL: Record<QueueReason, string> = {
  CALLBACK_DUE: 'Callback due',
  PTP_DUE_TODAY: 'Promise today',
  PRIORITY: 'Priority',
}

export function TeleQueuePage() {
  const navigate = useNavigate()
  const { data: rows, isPending, isError } = useTeleQueue()
  const { data: score } = useTeleScorecard()

  const callbacks = rows?.filter((r) => r.queueReason === 'CALLBACK_DUE').length ?? 0
  const promises = rows?.filter((r) => r.queueReason === 'PTP_DUE_TODAY').length ?? 0
  const blocked = rows?.filter((r) => r.conductBlocks.length > 0).length ?? 0
  const firstOpenable = rows?.find((r) => r.conductBlocks.length === 0)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">Telecalling</p>
          <h1 className="text-xl font-semibold tracking-tight text-navy-900">My queue</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Worked top to bottom: callbacks we promised, then promises landing today, then priority.
          </p>
          <FreshnessBadge className="mt-2" />
        </div>
        <Button
          disabled={!firstOpenable}
          onClick={() => firstOpenable && navigate({ to: '/tele/console/$caseId', params: { caseId: firstOpenable.caseId } })}
        >
          <PhoneCall className="mr-1.5 h-3.5 w-3.5" />
          Start calling
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="In queue" value={rows ? String(rows.length) : '—'} hint="Your TELE allocations" />
        <Kpi label="Callbacks due" value={String(callbacks)} hint="We promised a time" />
        <Kpi label="Promises today" value={String(promises)} hint="PTP lands today" />
        <Kpi
          label="Blocked"
          value={String(blocked)}
          hint={blocked > 0 ? 'DNC, hours or frequency' : 'Nothing blocked'}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Today</CardTitle>
          <CardDescription>
            Your calls only — phone work, counted separately from field visits.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Attempts" value={score ? String(score.attempts) : '—'} />
            <Stat label="Connected" value={pct(score?.connectRate)} sub={score ? `${score.connects}` : undefined} />
            <Stat label="Right party" value={pct(score?.rpcRate)} sub={score ? `${score.rightPartyContacts}` : undefined} />
            <Stat label="Promises" value={score ? String(score.promises) : '—'} sub={score && score.promises > 0 ? inr(score.promisedValue) : undefined} />
            <Stat label="Callback kept" value={pct(score?.callbackAdherence)} sub={score ? `${score.callbacksDone}/${score.callbacksDone + score.callbacksMissed}` : undefined} />
            <Stat label="Bad numbers" value={pct(score?.badNumberRate)} sub={score ? `${score.badNumbers}` : undefined} />
          </div>
          <p className="mt-3 text-2xs text-muted-foreground">
            A dash means nothing to divide by yet, not zero. Calls logged before the channel
            derivation landed carried whatever the client claimed, so figures spanning that
            cutover are unreliable.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Queue</CardTitle>
          <CardDescription>
            Blocked cases stay listed and greyed, with the reason — they are not hidden.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isPending && <div className="p-4"><SkeletonRows rows={6} /></div>}
          {isError && (
            <p className="p-4 text-sm text-muted-foreground">
              Could not load the queue. If you are not a telecaller, you will not have one.
            </p>
          )}
          {rows && rows.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-sm text-navy-900">Nothing allocated to you on the tele slot.</p>
              {/* The dead end that cost time: a manager holds CALL_LOG, so this
                  page appears for them, but they have no TELE allocations — so
                  the queue is empty and the console is unreachable, with no
                  explanation. Say who the queue belongs to. */}
              <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                This queue is your own TELE allocations, not your team's. If you manage a floor
                rather than work one, allocate cases to a telecaller on the{' '}
                <Link to="/allocations" className="text-brand hover:underline">Allocations</Link>{' '}
                screen — the console opens from a queue row.
              </p>
            </div>
          )}
          {rows && rows.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-2xs uppercase tracking-wider text-navy-600">
                  <th className="px-4 py-2 text-left font-semibold">Why</th>
                  <th className="px-4 py-2 text-left font-semibold">Loan account</th>
                  <th className="px-4 py-2 text-left font-semibold">Customer</th>
                  <th className="px-4 py-2 text-right font-semibold">DPD</th>
                  <th className="px-4 py-2 text-left font-semibold">Bucket</th>
                  <th className="px-4 py-2 text-right font-semibold">Amount due</th>
                  <th className="px-4 py-2 text-left font-semibold">Today</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <QueueRow key={r.caseId} row={r} onOpen={() => navigate({ to: '/tele/console/$caseId', params: { caseId: r.caseId } })} />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/** A dash for a null rate. See TeleScorecardService: null means no denominator. */
function pct(rate: string | null | undefined): string {
  if (rate === null || rate === undefined) return '—'
  return `${Math.round(Number(rate) * 100)}%`
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string | undefined }) {
  return (
    <div>
      <p className="text-2xs uppercase tracking-wider text-navy-600">{label}</p>
      <p className="text-lg font-semibold tabular-nums text-navy-900">{value}</p>
      {sub && <p className="text-2xs text-muted-foreground tabular-nums">{sub}</p>}
    </div>
  )
}

function QueueRow({ row, onOpen }: { row: TeleQueueRow; onOpen: () => void }) {
  const firstBlock = row.conductBlocks[0]
  const blocked = !!firstBlock
  return (
    <tr className={cn('border-b border-slate-100 last:border-0', blocked && 'bg-slate-50/70')}>
      <td className="px-4 py-2.5">
        <ReasonChip row={row} />
      </td>
      <td className="px-4 py-2.5 font-mono text-xs text-navy-800">{row.loanAccountNo}</td>
      <td className="px-4 py-2.5">
        <span className={cn('text-navy-900', blocked && 'text-muted-foreground')}>{row.customerName}</span>
        {row.preferredLanguage && !isDefaultLanguage(row.preferredLanguage) && (
          <span className="ml-1.5 text-2xs tracking-wide text-navy-600">
            {languageLabel(row.preferredLanguage)}
          </span>
        )}
        {firstBlock && (
          <span className="mt-0.5 flex items-center gap-1 text-2xs text-warning">
            <AlertTriangle className="h-3 w-3" />
            {firstBlock.message}
          </span>
        )}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">{dpd(row.currentDpd)}</td>
      <td className="px-4 py-2.5">
        <Badge>{bucketLabel(row.currentBucket)}</Badge>
      </td>
      {/* The demand, not POS — the queue is a call list, and this is what the
          agent will ask for. */}
      <td className="px-4 py-2.5 text-right font-medium tabular-nums">
        {row.currentOverdue ? inr(row.currentOverdue) : '—'}
      </td>
      <td className="px-4 py-2.5 text-xs text-muted-foreground tabular-nums">
        {/* Against the tenant's conduct cap, so the agent sees the ceiling
            coming rather than discovering it on submit. */}
        {row.contactCountToday}/{row.frequencyCap}
      </td>
      <td className="px-4 py-2.5 text-right">
        <Button size="sm" variant="ghost" onClick={onOpen}>
          {blocked ? 'Open' : 'Call'}
          <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </td>
    </tr>
  )
}

function ReasonChip({ row }: { row: TeleQueueRow }) {
  if (row.queueReason === 'CALLBACK_DUE') {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-brand/30 bg-brand-soft px-1.5 py-0.5 text-2xs font-medium text-brand">
        <Clock className="h-3 w-3" />
        {row.callbackAt ? new Date(row.callbackAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : REASON_LABEL.CALLBACK_DUE}
      </span>
    )
  }
  if (row.queueReason === 'PTP_DUE_TODAY') {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-positive/30 bg-positive-soft px-1.5 py-0.5 text-2xs font-medium text-positive">
        <HandCoins className="h-3 w-3" />
        {row.ptpAmount ? inr(row.ptpAmount) : REASON_LABEL.PTP_DUE_TODAY}
      </span>
    )
  }
  return (
    <span className="text-2xs uppercase tracking-wider text-navy-600">{REASON_LABEL.PRIORITY}</span>
  )
}
